import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Event } from '../../types/event.types';
import './card.css';

import { sortEvents } from '../../utils/eventSorting';
import { optimizeCloudinaryUrl } from '../../services/cloudinary.service';
import {
  Tag,
  Icon,
  FavoriteButton,
  ShareButton,
  DirectionsButton,
  AddToCalendarButton,
  NotificationBubble,
  LogoSVG,
  DateIcon,
  GlassPanel
} from '../../pages/DesignSystem';

import { getTagFallbackGradient, sortTags } from '../../pages/DesignSystem/types/tags.types';
import { useFavorites, useHiddenEvents } from '../../stores/appStore';
import { useWouterRouter } from '../../hooks/useWouterNavigation';
import { SwipeableCard } from '../../pages/DesignSystem/patterns/Modal/BaseModal';
import { formatEventDateTime, formatEventDateForIcon, formatEventTime } from '../../utils/timezoneUtils';
import { isEventPast } from '../../utils/eventUtils';
import { useDistance } from '../../hooks/useDistance';
import { useAnalytics } from '../../contexts/AnalyticsContext';

interface EventCardProps {
  event: Event;
  userLocation?: {
    latitude: number;
    longitude: number;
  };
  onDismiss?: (eventId: string) => void;
  onFavorite?: (eventId: string) => void;
  seriesEvents?: Event[];
  onClick?: (event: Event, seriesEvents?: Event[]) => void;
  disableSwipe?: boolean;
  isFirstCard?: boolean; // Pour l'animation wiggle d'onboarding
  isVisible?: boolean; // Pour savoir si la carte est visible (vue liste vs map)
}

// ✅ FIX: Comparateur custom pour détecter les modifications d'events en real-time
const areEventCardPropsEqual = (prevProps: EventCardProps, nextProps: EventCardProps): boolean => {
  // Comparer event par ID + updatedAt (pour détecter les modifications)
  const prevUpdatedAt = prevProps.event.updatedAt instanceof Date
    ? prevProps.event.updatedAt.getTime()
    : (prevProps.event.updatedAt || '');
  const nextUpdatedAt = nextProps.event.updatedAt instanceof Date
    ? nextProps.event.updatedAt.getTime()
    : (nextProps.event.updatedAt || '');

  if (prevProps.event.id !== nextProps.event.id || prevUpdatedAt !== nextUpdatedAt) {
    return false; // Event modifié = re-render nécessaire
  }

  // Comparer seriesEvents par longueur et updatedAt du premier
  if (prevProps.seriesEvents?.length !== nextProps.seriesEvents?.length) {
    return false;
  }

  // Comparer les autres props
  if (
    prevProps.userLocation?.latitude !== nextProps.userLocation?.latitude ||
    prevProps.userLocation?.longitude !== nextProps.userLocation?.longitude ||
    prevProps.disableSwipe !== nextProps.disableSwipe ||
    prevProps.isFirstCard !== nextProps.isFirstCard ||
    prevProps.isVisible !== nextProps.isVisible
  ) {
    return false;
  }

  return true; // Props identiques = pas de re-render
};

const EventCard = React.memo(({
  event,
  userLocation,
  onDismiss,
  onFavorite,
  seriesEvents,
  onClick,
  disableSwipe = false,
  isFirstCard = false,
  isVisible = true
}: EventCardProps) => {
  const { navigate, location } = useWouterRouter();

  const sortedSeriesEvents = useMemo(() => {
    if (!seriesEvents) return undefined;
    // Filtrer d'abord les événements passés
    const upcomingEvents = seriesEvents.filter(event => event.datetime && !isEventPast(event.datetime));
    // Puis utiliser la fonction de tri existante
    return sortEvents(upcomingEvents, 'list');
  }, [seriesEvents]);

  const [imageLoaded, setImageLoaded] = useState(false);
  const [showNotif, setShowNotif] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  const { toggleFavorite, isFavorite } = useFavorites();
  const { addToHiddenEvents } = useHiddenEvents();

  const imageRef = useRef<HTMLImageElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const analytics = useAnalytics();
  const hasImage = Boolean(event.image);
  const isCancelled = event.status === 'cancelled';

  // Fallback: reveal card after timeout if image hasn't loaded yet
  useEffect(() => {
    if (!hasImage || imageLoaded) return;
    const timer = setTimeout(() => setImageLoaded(true), 1500);
    return () => clearTimeout(timer);
  }, [hasImage, imageLoaded]);

  // Optimized image URL for card display (800px max width)
  const optimizedImageUrl = useMemo(
    () => optimizeCloudinaryUrl(event.image, { width: 800 }),
    [event.image]
  );

  useEffect(() => {
    // Intersection Observer pour le lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && imageRef.current) {
            imageRef.current.src = optimizedImageUrl;
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '300px' }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => observer.disconnect();
  }, [optimizedImageUrl]);

  useEffect(() => {
    // ✅ SIMPLIFIÉ : AnalyticsService gère maintenant les doublons
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // ✅ Toujours appeler - AnalyticsService filtre les doublons
            analytics.trackEventAppearance(event, 'card');
          }
        });
      },
      { threshold: 0.5 } // 50% visible
    );

    const cardElement = cardRef.current;
    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => observer.disconnect();
  }, [event.id, analytics, event]);

  // ✅ PERF FIX: Extraire les valeurs stables de l'event pour éviter re-renders
  const eventId = event.id;
  const eventSeriesId = event.seriesId;

  const distance = useDistance(userLocation, {
    latitude: event.latitude,
    longitude: event.longitude
  });

  // ✅ PERF FIX: handleCardClick défini AVANT handleClick (ordre d'exécution)
  const handleCardClick = useCallback(() => {
    analytics.trackEventClick(event, 'card');
    onClick?.(event, seriesEvents);
  }, [event, analytics, onClick, seriesEvents]);

  const handleClick = useCallback(() => {
    // Déclencher le tracking analytics
    handleCardClick();

    if (onClick) {
      // onClick est déjà appelé dans handleCardClick, donc on ne l'appelle pas ici
      return;
    } else {
      // Utiliser le pathname actuel pour déterminer le contexte
      const currentPath = location.pathname;
      const basePath = currentPath.startsWith('/favorites') ? '/favorites' : '';

      // ✅ Navigation intelligente : replace si on change d'event, push si nouvelle ouverture
      const isChangingEvent = currentPath.startsWith('/event/') || currentPath.startsWith('/series/');

      if (eventSeriesId) {
        navigate(`${basePath}/series/${eventSeriesId}`, {
          state: {
            isMapView: false,
            userLocation,
            seriesEvents: sortedSeriesEvents
          },
          replace: isChangingEvent  // Replace si on change d'event, push si nouvelle ouverture
        });
      } else {
        navigate(`${basePath}/event/${eventId}`, {
          state: {
            isMapView: false,
            userLocation
          },
          replace: isChangingEvent  // Replace si on change d'event, push si nouvelle ouverture
        });
      }
    }
  }, [onClick, sortedSeriesEvents, eventId, eventSeriesId, navigate, userLocation, location.pathname, handleCardClick]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  const handleExpandImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMobile) {
      setShowLightbox(true);
    } else {
      window.open(event.image || '', '_blank');
    }
  }, [isMobile, event.image]);

  // ✅ PERF FIX: Mémoiser les éléments de rendu pour éviter re-renders
  const tagsElement = useMemo(() => (
    <div className="event-card__tags">
      {sortTags(event.tags).map(tag => (
        <Tag
          key={tag}
          label={tag}
          type="activity"
          size="md"
        />
      ))}
    </div>
  ), [event.tags]);

  const headerElement = useMemo(() => (
    <div className="event-card__header">
      {tagsElement}
      <div className="event-card__actions" onClick={e => e.stopPropagation()}>
        <ShareButton
          shareData={{
            title: event.title,
            text: `what2do.today\nCome discover ${event.title},\non ${formatEventDateTime(event.datetime, event.timezone).date} at ${formatEventDateTime(event.datetime, event.timezone).time}\n`,
            url: window.location.href
          }}
          event={event}
          analyticsContext="card"
          variant="icon"
          size="lg"
          shouldTriggerSuccess={false}
          onShareSuccess={() => setShowNotif(true)}
        />
        <FavoriteButton
          id={event.id}
          seriesId={event.seriesId}
          organizerId={event.organizerId}
          expiresAt={event.datetime}
          fullEvent={event}
          size="lg"
          analyticsContext="card"
        />
      </div>
    </div>
  ), [tagsElement, event.title, event.datetime, event.timezone, event.id, event.seriesId, event.organizerId]);

  // ✅ PERF FIX: Mémoiser le prix
  const priceElement = useMemo(() => {
    // No prices at all → show nothing (scraped events with unknown price)
    if (!event.prices || event.prices.length === 0) {
      return null;
    }

    // Check if all prices have amount === 0 → Free
    const allFree = event.prices.every(price => price.amount === 0);
    if (allFree) {
      return <span className="event-card__glass-price event-card__glass-price--free">Free</span>;
    }

    // Sort prices: null amounts last, then by amount ascending
    const sortedPrices = [...event.prices].sort((a, b) => {
      if (a.amount === null && b.amount === null) return 0;
      if (a.amount === null) return 1;
      if (b.amount === null) return -1;
      return a.amount - b.amount;
    });

    return (
      <>
        {sortedPrices.map((price, index) => (
          <span key={index} className={`event-card__glass-price${price.amount === null ? ' event-card__glass-price--info' : ''}`}>
            {price.amount === null
              ? (price.description || 'Contact organizer')
              : price.amount === 0
                ? 'Free'
                : `${price.amount}${price.currency === 'EUR' ? '€' : price.currency}`}
            {price.amount !== null && price.description && ` - ${price.description}`}
          </span>
        ))}
      </>
    );
  }, [event.prices]);

  // ========== RENDU AVEC IMAGE (nouveau design) ==========
  const renderWithImage = () => (
    <div
      ref={cardRef}
      className={`event-card event-card--with-image ${isCancelled ? 'event-card--cancelled' : ''} ${imageLoaded ? 'event-card--revealed' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${event.title}`}
    >
      {/* Image de fond */}
      <div className="event-card__background">
        <img
          ref={imageRef}
          alt={event.title}
          className={imageLoaded ? 'loaded' : ''}
          onLoad={handleImageLoad}
          loading="lazy"
        />
      </div>

      {/* Header avec tags et actions - ✅ PERF: utilise élément mémorisé */}
      {headerElement}

      {/* Spacer pour pousser le GlassPanel vers le bas + bouton expand */}
      <div className="event-card__spacer">
        <button
          className="event-card__expand-image"
          onClick={handleExpandImage}
          aria-label="Agrandir l'image"
        >
          <Icon name="Fullscreen" size={20} />
        </button>
      </div>

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Badge Annulé si l'event est cancelled */}
            {isCancelled && (
              <span className="event-card__cancelled-badge">Cancelled</span>
            )}
            {/* Titre */}
            <h3 className={`event-card__glass-title ${isCancelled ? 'event-card__glass-title--cancelled' : ''}`}>{event.title}</h3>

            {/* Date et heure */}
            {event.datetime && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <DateIcon
                    {...formatEventDateForIcon(event.datetime, event.timezone)}
                    variant="dark"
                    size="sm"
                  />
                </div>
                <div className="event-card__glass-location-row">
                  <div className="event-card__glass-info">
                    <span className="event-card__glass-primary">
                      <strong>{formatEventDateTime(event.datetime, event.timezone).weekday}</strong>
                    </span>
                    <span className="event-card__glass-secondary">
                      {formatEventDateTime(event.datetime, event.timezone).time}
                      {event.endTime && ` - ${formatEventTime(event.endTime, event.timezone)}`}
                    </span>
                  </div>
                  <AddToCalendarButton
                    event={event}
                    analyticsContext="card"
                  />
                </div>
              </div>
            )}

            {/* Lieu + distance + navigation */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {event.locationName || event.location}
                  </span>
                  {distance && (
                    <span className="event-card__glass-location-distance">
                      · {distance}
                    </span>
                  )}
                </div>
                {event.latitude && event.longitude && (
                  <DirectionsButton
                    latitude={event.latitude}
                    longitude={event.longitude}
                    locationName={event.locationName || event.location || 'Event'}
                    event={event}
                    analyticsContext="card"
                  />
                )}
              </div>
            </div>

            {/* Prix - ✅ PERF: utilise élément mémorisé */}
            {priceElement && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <Icon name="Ticket" size={20} />
                </div>
                <div className="event-card__glass-info">
                  {priceElement}
                </div>
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  // Dégradé de fond basé sur les tags (cards sans image)
  const fallbackGradient = useMemo(() => getTagFallbackGradient(event.tags), [event.tags]);

  // ========== RENDU SANS IMAGE (même design avec logo fallback en fond) ==========
  const renderWithoutImage = () => (
    <div
      ref={cardRef}
      className={`event-card event-card--with-image event-card--fallback event-card--revealed ${isCancelled ? 'event-card--cancelled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`View details for ${event.title}`}
    >
      {/* Logo en fond - dégradé teinté selon les tags de l'event */}
      <div
        className="event-card__background event-card__background--fallback"
        style={fallbackGradient ? { background: fallbackGradient } : undefined}
      >
        <LogoSVG
          variant="vertTexte"
          size="auto"
          padding={0}
        />
      </div>

      {/* Header avec tags et actions - ✅ PERF: utilise élément mémorisé */}
      {headerElement}

      {/* GlassPanel avec les infos */}
      <div className="event-card__glass-container">
        <GlassPanel variant="light" blur="md" radius="md">
          <div className="event-card__glass-content">
            {/* Badge Annulé si l'event est cancelled */}
            {isCancelled && (
              <span className="event-card__cancelled-badge">Cancelled</span>
            )}
            {/* Titre */}
            <h3 className={`event-card__glass-title ${isCancelled ? 'event-card__glass-title--cancelled' : ''}`}>{event.title}</h3>

            {/* Date et heure */}
            {event.datetime && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <DateIcon
                    {...formatEventDateForIcon(event.datetime, event.timezone)}
                    variant="dark"
                    size="sm"
                  />
                </div>
                <div className="event-card__glass-location-row">
                  <div className="event-card__glass-info">
                    <span className="event-card__glass-primary">
                      <strong>{formatEventDateTime(event.datetime, event.timezone).weekday}</strong>
                    </span>
                    <span className="event-card__glass-secondary">
                      {formatEventDateTime(event.datetime, event.timezone).time}
                      {event.endTime && ` - ${formatEventTime(event.endTime, event.timezone)}`}
                    </span>
                  </div>
                  <AddToCalendarButton
                    event={event}
                    analyticsContext="card"
                  />
                </div>
              </div>
            )}

            {/* Lieu + distance + navigation */}
            <div className="event-card__glass-row">
              <div className="event-card__glass-icon">
                <Icon name="MapPin" size={20} />
              </div>
              <div className="event-card__glass-location-row">
                <div className="event-card__glass-location-text">
                  <span className="event-card__glass-location-name">
                    {event.locationName || event.location}
                  </span>
                  {distance && (
                    <span className="event-card__glass-location-distance">
                      · {distance}
                    </span>
                  )}
                </div>
                {event.latitude && event.longitude && (
                  <DirectionsButton
                    latitude={event.latitude}
                    longitude={event.longitude}
                    locationName={event.locationName || event.location || 'Event'}
                    event={event}
                    analyticsContext="card"
                  />
                )}
              </div>
            </div>

            {/* Prix - ✅ PERF: utilise élément mémorisé */}
            {priceElement && (
              <div className="event-card__glass-row">
                <div className="event-card__glass-icon">
                  <Icon name="Ticket" size={20} />
                </div>
                <div className="event-card__glass-info">
                  {priceElement}
                </div>
              </div>
            )}
          </div>
        </GlassPanel>
      </div>
    </div>
  );

  return (
    <SwipeableCard
      onLike={async () => {
        const storageId = event.seriesId || event.id;

        // ✅ Si déjà en favori, ne rien faire (évite le toggle involontaire)
        if (isFavorite(storageId)) {
          console.log('🧪 [Card] onLike ignoré - déjà en favori:', storageId);
          return;
        }

        console.log('🧪 [Card] onLike début:', { eventId: event.id, seriesId: event.seriesId, storageId });

        // ✅ CENTRALISÉ : toggleFavorite gère maintenant favoris + hiddenEvents
        await toggleFavorite(storageId, event.datetime, undefined, event);
        console.log('🧪 [Card] toggleFavorite terminé (gère favoris + hiddenEvents)');

        if (onFavorite) {
          onFavorite(event.id);
        }
      }}
      onDismiss={async () => {
        // ✅ CORRECTION : Ajouter à hiddenEvents avec raison 'dismiss'
        await addToHiddenEvents(event.seriesId || event.id, 'dismiss');
        if (onDismiss) {
          onDismiss(event.id);
        }
      }}
      eventId={event.id}
      seriesId={event.seriesId}
      organizerId={event.organizerId}
      fullEvent={event}
      disableSwipe={disableSwipe}
      isFirstCard={isFirstCard}
      isVisible={isVisible}
    >
      {hasImage ? renderWithImage() : renderWithoutImage()}

      <NotificationBubble
        show={showNotif}
        message="Link copied! ✨"
        type="success"
        duration={3000}
        onHide={() => setShowNotif(false)}
      />

      {/* Lightbox pour affichage plein écran sur mobile */}
      {showLightbox && event.image && (
        <div
          className="event-card__lightbox"
          onClick={() => setShowLightbox(false)}
        >
          <button
            className="event-card__lightbox-close"
            onClick={() => setShowLightbox(false)}
            aria-label="Fermer"
          >
            <Icon name="X" size={24} />
          </button>
          <img
            src={optimizeCloudinaryUrl(event.image, { width: 1200 })}
            alt={event.title}
            className="event-card__lightbox-image"
          />
        </div>
      )}
    </SwipeableCard>
  );
}, areEventCardPropsEqual);

export default EventCard;
